/**
 * Directory view-model — verbatim port of renderVals' directory pipeline:
 * query/role/status filter → per-item mapping → grouping (none/product/
 * company/adapter/status) with localeCompare sort.
 */
import { useMemo } from 'react';
import { useBridge } from '../../store/bridgeStore';
import { allInts } from '../../domain/catalog';
import type { Integration } from '../../domain/types';

export type DirItem = Integration & {
  connected: boolean;
  status: string | null;
  vendorVersion: string | null;
  statusLabel: string;
  tone: string;
  draft: boolean;
  clsLabel: string;
  kindLabel: string;
  kindTone: string;
  category: string;
  company: string;
  chips: { group: string; value: string }[];
  labels: { group: string; value: string }[];
};

export type DirGroup = { key: string; showHeader: boolean; count: number; items: DirItem[] };

export function useDirectory() {
  const { state } = useBridge();

  return useMemo(() => {
    const s = state;
    const q = s.query.trim().toLowerCase();
    const isSoftknack = s.role === 'softknack';
    const getConn = (id: string) => s.conn[id] || { connected: false, status: null, vendorVersion: null };

    const items: DirItem[] = allInts(s.adapters, s.metaOverrides)
      .filter(i => !q || i.name.toLowerCase().includes(q) || i.purpose.toLowerCase().includes(q))
      .filter(i => isSoftknack || !(i._custom && !i._published))
      .filter(i => {
        if (s.filter === 'all') return true;
        const c = getConn(i.id);
        if (s.filter === 'connected') return c.connected;
        if (s.filter === 'available') return !c.connected;
        return true;
      })
      .map(i => {
        const c = getConn(i.id);
        let statusLabel: string;
        let tone: string;
        let draft = false;
        if (i._custom && !i._published) {
          statusLabel = 'Not published';
          tone = 'warn';
          draft = true;
        } else {
          statusLabel = c.connected
            ? c.status === 'active'
              ? 'Connected'
              : c.status === 'expired'
              ? 'Re-auth needed'
              : 'Revoked'
            : 'Available';
          tone = c.connected ? (c.status === 'active' ? 'success' : 'warn') : 'neutral';
        }
        const clsLabel = i.cls.toUpperCase();
        const isMcp = i.cls === 'mcp';
        const kindLabel = isMcp ? 'MCP' : 'API';
        const kindTone = isMcp ? 'success' : 'info';
        const category = i.category || 'Custom';
        const company = i.company || i.name;
        const chips = [{ group: 'Company', value: company }, { group: 'Type', value: category }];
        const labels = [
          { group: 'Product type', value: category },
          { group: 'Company', value: company },
          { group: 'Adapter', value: clsLabel + ' adapter' },
          { group: 'Change delivery', value: i.change || 'poll' },
        ];
        return { ...i, connected: c.connected, status: c.status, vendorVersion: c.vendorVersion, statusLabel, tone, draft, clsLabel, kindLabel, kindTone, category, company, chips, labels };
      });

    const groupKey = (i: DirItem) => {
      if (s.groupBy === 'product') return i.category;
      if (s.groupBy === 'company') return i.company;
      if (s.groupBy === 'adapter') return i.clsLabel + ' adapter';
      if (s.groupBy === 'status') return i.statusLabel;
      return '';
    };

    let groups: DirGroup[];
    if (s.groupBy === 'none') {
      groups = [{ key: '', showHeader: false, count: items.length, items }];
    } else {
      const order: string[] = [];
      const byKey: Record<string, DirItem[]> = {};
      items.forEach(i => {
        const k = groupKey(i) || '—';
        if (!byKey[k]) {
          byKey[k] = [];
          order.push(k);
        }
        byKey[k].push(i);
      });
      order.sort((a, b) => a.localeCompare(b));
      groups = order.map(k => ({ key: k, showHeader: true, count: byKey[k].length, items: byKey[k] }));
    }

    return { groups, count: items.length };
  }, [state]);
}
